import {apiClient} from "../../connection/BlinkApiClient.js";
import {loadVersionedObjectFromResponse} from "../misc/StoreUtils";
import {PANEL_TYPE} from "../../../blink-sdk/Constants.js";
import {MerchantSDKResource, type BaseSDKResourceRequest, type SDKResourceRequest} from "../misc/MerchantSDKResource.js";
import {BaseStore, globalStore} from "../../../stem-core/state/Store.js";
import type {StoreId} from "../../../stem-core/state/State";
import type {PanelPromptOptions} from "../../../blink-sdk/PanelOptionsLogic";


@globalStore
export class MerchantPanel extends BaseStore("MerchantPanel", {}, MerchantSDKResource) {
    declare alias: string;
    declare options: PanelPromptOptions;
    declare name: string;
    // Set by getMerchantPanelsForVersion on the placeholder base panels it builds client-side
    declare isMocked?: boolean;

    isBasePanel(): boolean {
        // Widened for the membership test: alias is an arbitrary string, while Object.values on a const
        // table answers with a tuple of its literals
        return this.alias === "base" || (Object.values(PANEL_TYPE) as string[]).indexOf(this.alias) >= 0 && this.alias !== PANEL_TYPE.custom;
    }

    getParentPanel(): MerchantPanel | null {
        return this.getStore().getByIdOrAlias(this.options.parentPanel);
    }

    usesPanel(panel: MerchantPanel): boolean {
        const parentPanel = this.getParentPanel();
        if (parentPanel == null) {
            return false;
        }
        return parentPanel === panel || parentPanel.usesPanel(panel);
    }
}

// EditMerchantPanelRequest (api/merchants/deserializers.py:269) adds the panel being edited
interface EditMerchantPanelRequest extends SDKResourceRequest {
    panelId: StoreId;
}

// MerchantPanelRequest (api/merchants/deserializers.py:265) names the panel and its settings version, nothing else
interface MerchantPanelRequest extends BaseSDKResourceRequest {
    panelId: StoreId;
}

export async function apiCreateMerchantPanel(data: SDKResourceRequest): Promise<MerchantPanel> {
    const response = await apiClient.post("/merchant/create_panel", data);
    return loadVersionedObjectFromResponse(MerchantPanel, response, null);
}

export async function apiEditMerchantPanel(data: EditMerchantPanelRequest): Promise<MerchantPanel> {
    const response = await apiClient.post("/merchant/edit_panel", data);
    return loadVersionedObjectFromResponse(MerchantPanel, response, data.panelId);
}

export async function apiDeleteMerchantPanel(data: MerchantPanelRequest): Promise<any> {
    return apiClient.post("/merchant/delete_panel", data);
}

function getPanelOrderIndex(panel: MerchantPanel): number {
    if (!panel.isBasePanel()) {
        return 3;
    }
    if (panel.alias !== "base") {
        return 2;
    }
    return 1;
}

export function panelSortCompareFn(panel1: MerchantPanel, panel2: MerchantPanel): number {
    if (getPanelOrderIndex(panel1) !== getPanelOrderIndex(panel2)) {
        return getPanelOrderIndex(panel1) - getPanelOrderIndex(panel2);
    }
    return panel1.name < panel2.name ? -1 : 1;
}
